/**
 * The ONE place energy and mass convert (SPEC rule 1). A second conversion site anywhere in
 * the codebase is a bug: kJ-led labels must round-trip exactly.
 */
import { KJ_PER_KCAL } from './types'

export const G_PER_OZ = 28.3495
/** The international avoirdupois pound, exactly. */
export const G_PER_LB = 453.59237

export type EnergyUnit = 'kJ' | 'kcal' | 'both'

export function kcalToKj(kcal: number): number {
  return kcal * KJ_PER_KCAL
}

export function kjToKcal(kj: number): number {
  return kj / KJ_PER_KCAL
}

/**
 * How far a stated kJ may sit from the stated kcal × 4.184, as a share of the larger: labels
 * round both. The one tolerance every reader and `check` share, so nothing an importer accepts
 * is an error in check.
 */
export const KJ_KCAL_TOLERANCE = 0.03

/** Whether a kcal and a kJ figure for the same food agree within KJ_KCAL_TOLERANCE. */
export function energyPairAgrees(kcal: number, kj: number): boolean {
  const expected = kcalToKj(kcal)
  return Math.abs(kj - expected) <= KJ_KCAL_TOLERANCE * Math.max(expected, kj)
}

export function ozToG(oz: number): number {
  return oz * G_PER_OZ
}

export function gToOz(g: number): number {
  return g / G_PER_OZ
}

export function lbToG(lb: number): number {
  return lb * G_PER_LB
}

/** Round to `dp` decimal places without float dust (4.559999999 → 4.56). */
export function round(n: number, dp = 2): number {
  if (!Number.isFinite(n)) return n
  const f = 10 ** dp
  return Math.round((n + Number.EPSILON * Math.sign(n)) * f) / f
}

/** Thousands with a thin-free comma, independent of the host locale so tests are stable. */
export function groupThousands(n: number): string {
  const sign = n < 0 ? '-' : ''
  const digits = String(Math.abs(Math.round(n)))
  return sign + digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/** "1,840 kcal", "7,699 kJ", or "1,840 kcal (7,699 kJ)". */
export function formatEnergy(kcal: number, unit: EnergyUnit = 'kcal'): string {
  const kcalText = `${groupThousands(kcal)} kcal`
  const kjText = `${groupThousands(kcalToKj(kcal))} kJ`
  if (unit === 'kJ') return kjText
  if (unit === 'both') return `${kcalText} (${kjText})`
  return kcalText
}

export function kgToLb(kg: number): number {
  return (kg * 1000) / G_PER_LB
}

export function lbToKg(lb: number): number {
  return lbToG(lb) / 1000
}

/** How mass is SHOWN. Storage is always grams. */
export type MassUnit = 'g' | 'oz'

/** Energy density per gram → per ounce (kcal/g → kcal/oz; kJ/g → kJ/oz). */
export function perGToPerOz(perG: number): number {
  return perG * G_PER_OZ
}

/** Energy density per ounce → per gram. */
export function perOzToPerG(perOz: number): number {
  return perOz / G_PER_OZ
}

/** Whole pounds and the ounces left over, ounces rounded so 15.96 oz carries to the pound. */
export function splitLbOz(g: number): { lb: number; oz: number } {
  const totalOz = Math.round(Math.abs(gToOz(g)))
  return { lb: Math.floor(totalOz / 16), oz: totalOz % 16 }
}

/**
 * Mass for display, locale-independent: "250 g", "1.26 kg"; "8.8 oz", "12 oz", "1 lb 13 oz".
 * `big` allows kg (≥ 1000 g) or lb + oz (≥ 1 lb), as a US hiker sheet writes a box's weight.
 */
export function formatMass(g: number, unit: MassUnit = 'g', opts: { big?: boolean } = {}): string {
  if (!Number.isFinite(g)) return '—'
  if (unit === 'g') {
    if (opts.big && Math.abs(g) >= 1000) return `${round(g / 1000, 2).toFixed(2)} kg`
    return `${groupThousands(g)} g`
  }
  const oz = gToOz(g)
  const sign = g < 0 ? '-' : ''
  if (opts.big && Math.abs(oz) >= 15.95) {
    const { lb, oz: rest } = splitLbOz(g)
    return `${sign}${groupThousands(lb)} lb${rest ? ` ${rest} oz` : ''}`
  }
  const abs = Math.abs(oz)
  return `${sign}${abs < 10 ? round(abs, 1) : groupThousands(abs)} oz`
}
